import React, { SetStateAction, useEffect, useLayoutEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Campaign } from '../../../model/campaign/Campaign';
import { CustomAutocomplete } from '../../../shared/components/Autocomplete';
import usePagination from '../../../shared/hooks/usePagination';
import { ErrorToast } from '../../../utils/toasters';
import ReportingPerCampaign from './components/ReportingPerCampaign';
import { IPerformanceSummary, IReportingSummary, IReportingTasksSummary } from './dto/IReporting';
import { InfluencerReportingService } from './service';
import AlertBox from '../../../shared/components/alert/AlertBox';
import ReportingOverview from './components/ReportingOverview';
import { ReportingOverviewSkeleton } from './components/ReportingSkeletons';
import { StoreKeeper } from '../../../store/store';
import { loadingSlice } from '../../../store/general/loadingSlice';

interface ILocation {
    campaignId: number;
}

const InfluencerReporting = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useTranslation();
    const { filterParams, setFilterParams } = usePagination({
        perPage: 10,
        otherKeys: {
            campaignId: (state as ILocation)?.campaignId ?? '',
            isCompleted: false,
        },
    });
    const [updateReports, setUpdateReports] = useState(false);
    const [summary, setSummary] = useState<IReportingSummary>();
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [selectedFilterCampaign, setSelectedFilterCampaign] = useState<Campaign>();
    const [isSummaryLoading, setIsSummaryLoading] = useState(true);

    const changeCampaignFilterHandler = (paramKey: string, paramValue: string | string[] | number) => {
        const selectedCampaign = campaigns?.find(campaign => campaign?.offerId === +paramValue);
        setFilterParams((prev: any) => ({
            ...prev,
            page: 1,
            campaignId: selectedCampaign?.id,
            isLongTerm: selectedCampaign?.offers?.[0]?.isLongTerm ?? null,
        }));
        setSelectedFilterCampaign(selectedCampaign);
    };

    useEffect(() => {
        InfluencerReportingService.getSummary(filterParams)
            .then(response => {
                setSummary(response);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsSummaryLoading(false));
    }, [updateReports, campaigns]);

    useEffect(() => {
        InfluencerReportingService.getReportingCampaigns({ ...filterParams, page: 1 })
            .then(response => {
                if ((state as ILocation)?.campaignId) {
                    const campaignId = (state as ILocation)?.campaignId;
                    setSelectedFilterCampaign(response?.data?.find((campaign: Campaign) => campaign.id === campaignId));
                }
                setCampaigns(
                    response.data?.map((campaign: Campaign) => ({
                        ...campaign,
                        offerId: campaign?.offers?.[0]?.id,
                    }))
                );
            })
            .catch(error => ErrorToast(error));
    }, [filterParams]);

    const getCounter = (value?: number) => (isSummaryLoading ? '–' : value ?? 0);
    useLayoutEffect(() => {
        StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    });
    return (
        <Row className="pb-5">
            <AlertBox
                image="/icons/img-warning.svg"
                classCol="col-12 mb-4"
                variant="primary"
                title={t('influencer.invoice.invoice_alert_title')}
                desc={t('influencer.invoice.invoice_alert_desc')}
                textButton={t('influencer.invoice.go_to')}
                functionBtn={() => navigate('/influencer/invoices')}
            />
            <Col xs={12} className="mb-3 mt-md-0">
                <h4>{t('influencer.reporting.reporting_overview')}</h4>
                {isSummaryLoading ? (
                    <ReportingOverviewSkeleton />
                ) : (
                    <ReportingOverview
                        reportingTasks={summary?.reportingTasksSummary as IReportingTasksSummary}
                        performance={summary?.performanceSummary as IPerformanceSummary}
                    />
                )}
            </Col>
            <Col xs={12} className="order-1 order-md-3 mt-md-3">
                <div className="d-flex align-items-center justify-content-start flex-wrap">
                    <div className="w-md-30 w-100 d-flex">
                        <CustomAutocomplete
                            dataArray={campaigns}
                            isWithImage={false}
                            inputLabel="title"
                            setFilterState={setFilterParams}
                            disableClearable={false}
                            changeEventKey="name"
                            queryFilterKey="offerId"
                            key={selectedFilterCampaign?.id}
                            isLongSelected={selectedFilterCampaign?.offers?.[0]?.isLongTerm}
                            classes="w-100"
                            defaultArray={selectedFilterCampaign as unknown as Campaign[]}
                            inputLabelTranslation={t('influencer.planner.selectCampaign')}
                            handleChangeParams={changeCampaignFilterHandler}
                        />
                    </div>
                    <div className="mt-3 mt-md-0">
                        <Button
                            size="sm"
                            className="mx-md-3 mr-2"
                            variant={filterParams?.isCompleted ? 'outline-primary' : 'primary'}
                            onClick={() =>
                                setFilterParams((prevState: any) => ({ ...prevState, isCompleted: false, page: 1 }))
                            }
                        >
                            {t(`general.campaign_status.ongoing`)}
                            {` (${getCounter(summary?.reportingTasksSummary?.ongoingCampaigns)})`}
                        </Button>
                        <Button
                            size="sm"
                            variant={filterParams?.isCompleted ? 'primary' : 'outline-primary'}
                            onClick={() =>
                                setFilterParams((prevState: any) => ({ ...prevState, isCompleted: true, page: 1 }))
                            }
                        >
                            {t(`general.campaign_status.completed`)}
                            {` (${getCounter(summary?.reportingTasksSummary?.completedCampaigns)})`}
                        </Button>
                    </div>
                </div>
            </Col>
            <Col xs={12} className="order-4">
                <ReportingPerCampaign
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    setUpdateReports={setUpdateReports as React.Dispatch<SetStateAction<boolean>>}
                    updateReports={updateReports}
                />
            </Col>
        </Row>
    );
};

export default InfluencerReporting;
